import { Button, EnrollmentHelpText } from '@/shared/components'
import { EndorsementDetailsModal } from './additionalInfo/EndorsementDetailsModal'
import { EndorsementSelection } from './additionalInfo/EndorsementSelection'
import { AdditionalInfoStatus } from './additionalInfo/AdditionalInfoStatus'
import { TrainingPreferencesSection } from './additionalInfo/TrainingPreferencesSection'
import { useAdditionalInfoControls } from './additionalInfo/useAdditionalInfoControls'
import { useAdditionalInfoStep } from './additionalInfo/useAdditionalInfoStep'
import { StepHeader } from '../StepHeader'
import type { EligibilityFormData } from './types'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type AdditionalInfoStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onBack: () => void
  onContinue: (shouldShowLaptopQuestion: boolean) => void
  onContinueWithoutEndorsements: (shouldShowLaptopQuestion: boolean) => void
  isSubmitting?: boolean
  errorMessage?: string | null
}

export function AdditionalInfoStep({
  currentStep,
  totalSteps,
  fieldEditability,
  formData,
  updateFormData,
  onBack,
  onContinue,
  onContinueWithoutEndorsements,
  isSubmitting = false,
  errorMessage = null
}: AdditionalInfoStepProps) {
  const areEndorsementsDisabled = fieldEditability.endorsements === false
  const isTrainingModeDisabled = fieldEditability.trainingMode === false
  const isLaptopSelectionDisabled = fieldEditability.hasLaptop === false

  const {
    isPending,
    isError,
    activeEndorsement,
    setActiveEndorsement,
    selectionError,
    clearSelectionError,
    endorsementOptions,
    isInPersonAvailable,
    shouldShowLaptopQuestion,
    isTrainingQuestionIncomplete,
    toggleEndorsement,
    validateTrainingQuestions,
    isEndorsementSelected
  } = useAdditionalInfoStep({
    formData,
    updateFormData
  })
  const {
    handleContinue,
    handleContinueWithoutEndorsements,
    isContinueDisabled,
    isContinueWithoutEndorsementsDisabled
  } = useAdditionalInfoControls({
    endorsementCount: (formData.endorsements || []).length,
    isError,
    isPending,
    isSubmitting,
    isTrainingQuestionIncomplete,
    onContinue,
    onContinueWithoutEndorsements,
    shouldShowLaptopQuestion,
    validateTrainingQuestions
  })

  return (
    <div className="bg-white px-4 py-10">
      <div className="mx-auto">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-3 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">
            We are gathering interest on other types of vehicle training
          </h1>
        </div>

        <div className="pt-6 w-full max-w-2xl mx-auto">
          <div className="mx-auto max-w-5xl space-y-6">
            <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <EndorsementSelection
                endorsementOptions={endorsementOptions}
                disabled={areEndorsementsDisabled}
                formData={formData}
                onToggleEndorsement={toggleEndorsement}
                onOpenDetails={setActiveEndorsement}
              />
            </div>

            <TrainingPreferencesSection
              formData={formData}
              areTrainingModeOptionsDisabled={isTrainingModeDisabled}
              isLaptopSelectionDisabled={isLaptopSelectionDisabled}
              isInPersonAvailable={isInPersonAvailable}
              shouldShowLaptopQuestion={shouldShowLaptopQuestion}
              onUpdateFormData={updateFormData}
              onClearSelectionError={clearSelectionError}
            />
          </div>

          <AdditionalInfoStatus
            isPending={isPending}
            isError={isError}
            endorsementCount={endorsementOptions.length}
            errorMessage={errorMessage}
            selectionError={selectionError}
          />

          <div
            className="mt-8 flex flex-col items-center gap-3 animate-fade-in-up"
            style={{ animationDelay: '600ms' }}
          >
            <button
              type="button"
              onClick={handleContinue}
              disabled={isContinueDisabled}
              className="flex h-12 w-full max-w-[400px] items-center justify-center rounded-[50px] bg-black px-7 text-base font-semibold text-white shadow-[0_4px_12px_#0000001a] transition-all duration-200 hover:bg-black/85 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleContinueWithoutEndorsements}
              disabled={isContinueWithoutEndorsementsDisabled}
              className="w-full max-w-[400px] whitespace-nowrap hover:-translate-y-0.5 active:scale-95"
            >
              Continue Without Additional Interests
            </Button>
          </div>

          <div className="mt-5 text-center animate-fade-in-up" style={{ animationDelay: '760ms' }}>
            <EnrollmentHelpText />
          </div>
        </div>
      </div>
      <EndorsementDetailsModal
        endorsement={activeEndorsement}
        isSelected={isEndorsementSelected}
        onClose={() => setActiveEndorsement(null)}
        onToggle={toggleEndorsement}
      />
    </div>
  )
}
